import React, { useCallback, useRef } from 'react'

import ClientSideTable from '@/components/Table/ClientSideTable'
import QuotationDetailTableToolbar from '@/features/offers/offer-detail/components/QuotationDetailTableToolbar'
import {
  createQuotationDetailUpdateHandler,
  downloadQuotationDetailExcel,
  getDefaultQuotationCategoryAndSAPMaterialNumber,
} from '@/features/offers/offer-detail/helper/quotationDetailExcel.helper'
import { getQuotationTemplateDetailColumns } from '../../tabs/OfferSourcingTab/CostBudget'
import useZZZ6 from '@/features/zzz6/useZZZ6'
import { showModal } from '@/stores/ui/modalSlice'
import { excelFileTypes } from '@/utils/constants'
import { MODAL_TYPES } from '@/utils/constants/modalTypes'
import { getFormattedDataFromExcelFile } from '@/utils/fileHelper'
import { Flex } from '@chakra-ui/react'
import { CostBudgetType, getQuotationDetailTableColumnIds } from '@eapproval/offer-shared'
import { useDispatch } from 'react-redux'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { emptyQuotationTemplateRow } from '@/features/offerCostBudgets/constants/offerCostBudget.constant'

const CostBudgetQuotationDetailTable = ({ costBudgetType, data, disabled = false, excelAdditionalData = {} }) => {
  const dispatch = useDispatch()
  const { setValue, getValues } = useFormContext()
  const importExcelRef = useRef(null)
  const { useFetchZZZ6CategoryOptions, useFetchAllZZZ6s } = useZZZ6()

  const tableBasePath = 'quotationDetails'
  const { append: appendRow, remove: removeRow } = useFieldArray({
    name: tableBasePath,
  })

  const { data: zzz6Response } = useFetchAllZZZ6s({
    page: 1,
    pageSize: 100000,
    filters: { status: 'active' },
  })
  const { data: categoryOptions = [] } = useFetchZZZ6CategoryOptions({
    page: 1,
    pageSize: 100000,
    filters: { status: 'active' },
  })

  const disableCategory = [
    CostBudgetType.SUPPLY_ENGINEERING_SERVICE,
    CostBudgetType.SUPPLY_MAINTENANCE_SERVICE,
  ].includes(costBudgetType)

  const handleOpenECCNDetailModel = (ECCNDetail = []) => {
    dispatch(
      showModal({
        modalType: MODAL_TYPES.ECCN_DETAIL,
        modalProps: {
          ECCNDetail,
          disabled: true,
        },
      })
    )
  }

  const handleImportExcel = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    const tableColumns = getQuotationDetailTableColumnIds({
      costBudgetType,
      isTemplate: false,
      isExcel: true,
      canRemove: false,
    })

    const dataRows = await getFormattedDataFromExcelFile(file, tableColumns)

    setValue(tableBasePath, dataRows, { shouldDirty: true })

    if (importExcelRef.current) importExcelRef.current.value = ''
  }

  const handleRemoveQuotationTemplateRow = (index) => {
    if (disabled) return

    removeRow(index)
  }

  const columnDef = getQuotationTemplateDetailColumns({
    costBudgetType,
    handleRemove: handleRemoveQuotationTemplateRow,
    isTemplate: false,
    disabled: disabled,
    categoryOptions,
    disableCategory,
    handleOpenECCNDetailModel,
  })

  const handleDownloadQuotationDetail = useCallback(async () => {
    try {
      await downloadQuotationDetailExcel({
        costBudgetType,
        isTemplate: false,
        canRemove: false,
        rows: data,
        normalizeSelectColumns: true,
        excelAdditionalData,
      })
    } catch (err) {
      console.log(err)
    }
  }, [costBudgetType, data, excelAdditionalData])

  const handleUpdateQuotationTemplateDetails = createQuotationDetailUpdateHandler({
    disabled,
    setValue,
    getValues,
    zzz6Response,
    costBudgetType,
  })

  const handleAddQuotationTemplateRow = () => {
    if (disabled) return

    const { category: defaultCategory, SAPMaterialNumber: defaultSAPMaterialNumber } =
      getDefaultQuotationCategoryAndSAPMaterialNumber({
        costBudgetType,
        zzz6Response,
      })

    const currentValues = getValues()
    const quotationDetails = Array.isArray(currentValues?.quotationDetails) ? [...currentValues.quotationDetails] : []
    quotationDetails.push({
      ...emptyQuotationTemplateRow,
      category: defaultCategory,
      SAPMaterialNumber: defaultSAPMaterialNumber,
      quantity: 0,
      unitRate: 0,
      discount: 0,
      totalPrice: 0,
      ECCNDetail: [],
      numberOfWorker: 0,
    })
    setValue('quotationDetails', quotationDetails)
  }

  return (
    <Flex direction={'column'} gap="3">
      <QuotationDetailTableToolbar
        importExcelRef={importExcelRef}
        excelFileTypes={excelFileTypes}
        disabled={disabled}
        onImportExcel={handleImportExcel}
        onDownload={handleDownloadQuotationDetail}
        onAddRow={handleAddQuotationTemplateRow}
      />

      <ClientSideTable
        tableWidth="100%"
        columnDef={columnDef}
        data={data}
        disabled={disabled}
        updateData={handleUpdateQuotationTemplateDetails}
      />
    </Flex>
  )
}

export default CostBudgetQuotationDetailTable
